import React, { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import { Input } from "../../../../../components/input";
import FormErrorText from "../../../../../components/form/FormErrorText";
import { RequiredLabel } from "../../../../../components/form";
import { Textarea } from "../../../../../components/textarea";
import { SelectV2 } from "../../../../../components/select-v2";
import { cn } from "../../../../../lib/utils";
import { MerchantBusinessFormData, PartnerType } from "../../types/merchantRegister.types";
import { ImageUploadCropper } from "../../../../../components/image-upload-cropper";
import { useMerchantRegistrationOptions } from "../../hooks/useMerchantRegistrationOptions";
import { useCompleteMerchantOnboarding } from "../../hooks/useCompleteMerchantOnboarding";
import MerchantStepButtons from "../MerchantStepButtons";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../../../../components";
import { getApiErrorMessage } from "../../../../../lib/result-codes";
import LocationPicker, {
  LocationPickerValue,
} from "../../../../../components/customs/map/LocationPicker";
import { createMerchantBusinessSchema } from "../../schemas/merchantRegister.schemas";

export const Step4BusinessDetailsForm: React.FC = () => {
  const { t } = useTranslation();
  const {
    categoryOptions,
    error: optionsError,
    isLoading: isOptionsLoading,
    refetch,
  } = useMerchantRegistrationOptions();
  const completeOnboarding = useCompleteMerchantOnboarding();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [isOpen, setOpen] = useState(false);
  const [activeAddressIndex, setActiveAddressIndex] = useState(0);

  const {
    control,
    formState: { errors },
    setValue,
    handleSubmit,
  } = useForm<MerchantBusinessFormData>({
    resolver: zodResolver(createMerchantBusinessSchema(t)),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      partnerType: PartnerType.None,
      addresses: [{ address1: "", city: "", state: "", zipcode: "" }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "addresses",
  });

  const handleAddAddress = () => {
    append({ address1: "", city: "", state: "", zipcode: "" });
  };

  const onSubmit = async (data: MerchantBusinessFormData) => {
    try {
      const result = await completeOnboarding.execute(data);
      if (result !== undefined) {
        navigate("/login", { replace: true });
      }
    } catch (error: unknown) {
      addToast({
        type: "error",
        title: t("merchantRegister.step4.title"),
        message: getApiErrorMessage(error),
      });
    }
  };

  const [location, setLocation] = useState<LocationPickerValue>({
    address: "",
    latitude: undefined,
    longitude: undefined,
    polygon: [],
  });

  return (
    <div className="w-full h-full flex flex-col justify-between">
      <div className="w-full">
        <h2 className="text-xl md:text-2xl font-bold text-black dark:text-white mb-6 md:mb-[54px] font-nunito text-center">
          {t("merchantRegister.step4.title")}
        </h2>

        {/* Logo Upload */}
        <div className="mb-4 md:mb-6">
          <Controller
            name="logoImage"
            control={control}
            render={({ field }) => (
              <ImageUploadCropper
                value={field.value}
                onChange={field.onChange}
                error={errors.logoImage?.message}
                title={t("merchantRegister.step4.logoUpload")}
                description={t("merchantRegister.step4.logoUploadHint")}
                aspect={1}
                accept={{
                  "image/jpeg": [".jpg", ".jpeg"],
                  "image/png": [".png"],
                }}
                outputFileName="business-logo.jpg"
                previewShape="round"
              />
            )}
          />
        </div>

        {/* Business Name & Short Intro */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
          <div>
            <RequiredLabel
              htmlFor="businessName"
              className="block text-[#75757D] dark:text-gray-400 text-sm font-nunito mb-2"
              required
            >
              {t("merchantRegister.step4.businessName")}
            </RequiredLabel>
            <Controller
              name="businessName"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  id="businessName"
                  placeholder=""
                  onBlur={() => {
                    field.onBlur();
                  }}
                  className={cn(
                    "h-12 bg-white dark:bg-gray-800 border border-[#DCDCDC] dark:border-gray-600 rounded-lg font-nunito dark:text-white",
                    errors.businessName && "border-[#EC5454]",
                  )}
                />
              )}
            />
            <FormErrorText
              message={errors.businessName?.message}
              className="mt-[7px] font-semibold font-nunito"
            />
          </div>

          <div>
            <RequiredLabel
              htmlFor="businessShortIntro"
              className="block text-[#75757D] dark:text-gray-400 text-sm font-nunito mb-2"
              required
            >
              {t("merchantRegister.step4.businessShortIntro")}
            </RequiredLabel>
            <Controller
              name="businessShortIntro"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  id="businessShortIntro"
                  placeholder=""
                  onBlur={() => {
                    field.onBlur();
                  }}
                  className={cn(
                    "h-12 bg-white dark:bg-gray-800 border border-[#DCDCDC] dark:border-gray-600 rounded-lg font-nunito dark:text-white",
                    errors.businessShortIntro && "border-[#EC5454]",
                  )}
                />
              )}
            />
            <FormErrorText
              message={errors.businessShortIntro?.message}
              className="mt-[7px] font-semibold font-nunito"
            />
          </div>
        </div>

        {/* Description */}
        <div className="mb-4 md:mb-6">
          <label
            htmlFor="description"
            className="block text-[#75757D] dark:text-gray-400 text-sm font-nunito mb-2"
          >
            {t("merchantRegister.step4.description")}
          </label>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <Textarea
                {...field}
                id="description"
                placeholder={t("merchantRegister.step4.descriptionPlaceholder")}
                className="bg-white dark:bg-gray-800 border border-[#DCDCDC] dark:border-gray-600 rounded-lg font-nunito min-h-[80px] dark:text-white"
              />
            )}
          />
        </div>

        <div className="mb-4 md:mb-6">
          <RequiredLabel
            className="block text-[#75757D] dark:text-gray-400 text-sm font-nunito mb-2"
            required
          >
            {t("merchantRegister.step4.partnerType")}
          </RequiredLabel>
          <Controller
            name="partnerType"
            control={control}
            render={({ field }) => (
              <SelectV2
                value={field.value}
                onChange={(value) => field.onChange(value as PartnerType)}
                options={[
                  {
                    value: PartnerType.CitiesMunicipalities,
                    label: t("merchantRegister.step4.partnerTypeCitiesMunicipalities"),
                  },
                  {
                    value: PartnerType.LocalBusinesses,
                    label: t("merchantRegister.step4.partnerTypeLocalBusinesses"),
                  },
                  {
                    value: PartnerType.Corporate,
                    label: t("merchantRegister.step4.partnerTypeCorporate"),
                  },
                  {
                    value: PartnerType.NonprofitsCommunityOrganizations,
                    label: t("merchantRegister.step4.partnerTypeNonprofits"),
                  },
                ]}
                placeholder={t("merchantRegister.step4.selectPartnerType")}
                ariaLabel={t("merchantRegister.step4.partnerType")}
                triggerClassName={cn(
                  "h-12 bg-white dark:bg-gray-800 border border-[#DCDCDC] dark:border-gray-600 rounded-lg font-nunito dark:text-white",
                  errors.partnerType && "border-[#EC5454]",
                )}
              />
            )}
          />
          <FormErrorText
            message={errors.partnerType?.message}
            className="mt-[7px] font-semibold font-nunito"
          />
        </div>

        {/* Business Category & URL */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
          <div>
            <RequiredLabel
              className="block text-[#75757D] dark:text-gray-400 text-sm font-nunito mb-2"
              required
            >
              {t("merchantRegister.step4.businessCategory")}
            </RequiredLabel>
            <Controller
              name="businessCategory"
              control={control}
              render={({ field }) => (
                <SelectV2
                  value={field.value}
                  onChange={field.onChange}
                  options={categoryOptions.map((category) => ({
                    value: category.id,
                    textValue: category.name,
                    label: (
                      <span className="flex min-w-0 items-center gap-2">
                        {category.icon_url ? (
                          <img
                            src={category.icon_url}
                            alt=""
                            aria-hidden="true"
                            className="h-5 w-5 shrink-0 object-contain"
                          />
                        ) : null}
                        <span className="truncate">{category.name}</span>
                      </span>
                    ),
                  }))}
                  placeholder={t("merchantRegister.step4.selectCategory")}
                  ariaLabel={t("merchantRegister.step4.businessCategory")}
                  disabled={isOptionsLoading}
                  triggerClassName={cn(
                    "h-12 bg-white dark:bg-gray-800 border border-[#DCDCDC] dark:border-gray-600 rounded-lg font-nunito dark:text-white",
                    errors.businessCategory && "border-[#EC5454]",
                  )}
                />
              )}
            />
            {optionsError && (
              <div
                role="alert"
                className="mt-2 flex items-center justify-between gap-3 text-sm text-[#EC5454]"
              >
                <span>{optionsError.message}</span>
                <button
                  type="button"
                  onClick={() => void refetch()}
                  className="font-semibold underline"
                >
                  {t("ui.retry")}
                </button>
              </div>
            )}
            {isOptionsLoading && (
              <p
                role="status"
                className="mt-2 text-sm text-[#75757D] dark:text-gray-400"
              >
                {t("ui.loading")}
              </p>
            )}
            <FormErrorText
              message={errors.businessCategory?.message}
              className="mt-[7px] font-semibold font-nunito"
            />
          </div>

          <div>
            <label
              htmlFor="businessUrl"
              className="block text-[#75757D] dark:text-gray-400 text-sm font-nunito mb-2"
            >
              {t("merchantRegister.step4.businessUrl")}
            </label>
            <Controller
              name="businessUrl"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  id="businessUrl"
                  placeholder=""
                  onBlur={() => {
                    field.onBlur();
                  }}
                  className={cn(
                    "h-12 bg-white dark:bg-gray-800 border border-[#DCDCDC] dark:border-gray-600 rounded-lg font-nunito dark:text-white",
                    errors.businessUrl && "border-[#EC5454]",
                  )}
                />
              )}
            />
            <FormErrorText
              message={errors.businessUrl?.message}
              className="mt-[7px] font-semibold font-nunito"
            />
          </div>
        </div>

        {/* Addresses */}
        {fields.map((field, index) => (
          <div
            key={field.id}
            className="mb-4 md:mb-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
              <div>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <RequiredLabel
                    htmlFor={`address-${field.id}`}
                    className="block text-sm font-nunito text-[#75757D] dark:text-gray-400"
                    required
                  >
                    {t("merchantRegister.step4.address")}
                    {fields.length > 1 ? ` ${index + 1}` : ""}
                  </RequiredLabel>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        setActiveAddressIndex(index);
                        setOpen(true);
                      }}
                      className="text-sm font-semibold text-[#004232] underline hover:text-[#00A882] dark:text-[#00C49A]"
                    >
                      {t("merchantRegister.step4.pickLocation")}
                    </button>
                    {index > 0 && (
                      <button
                        type="button"
                        onClick={() => remove(index)}
                        aria-label={t("merchantRegister.step4.removeAddress")}
                        title={t("merchantRegister.step4.removeAddress")}
                        className="rounded p-1 text-[#EC5454] transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                <Controller
                  name={`addresses.${index}.address1`}
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id={`address-${fields[index].id}`}
                      placeholder={t("merchantRegister.step4.selectLocation")}
                      className={cn(
                        "h-12 bg-white dark:bg-gray-800 border border-[#DCDCDC] dark:border-gray-600 rounded-lg font-nunito dark:text-white",
                        errors.addresses?.[index]?.address1 && "border-[#EC5454]",
                      )}
                    />
                  )}
                />
                <FormErrorText
                  message={errors.addresses?.[index]?.address1?.message}
                  className="mt-[7px] font-semibold font-nunito"
                />
              </div>

              <div>
                <RequiredLabel
                  htmlFor={`city-${field.id}`}
                  className="block text-[#75757D] dark:text-gray-400 text-sm font-nunito mb-2"
                  required
                >
                  {t("merchantRegister.step4.city")}
                </RequiredLabel>
                <Controller
                  name={`addresses.${index}.city`}
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id={`city-${fields[index].id}`}
                      placeholder=""
                      className={cn(
                        "h-12 bg-white dark:bg-gray-800 border border-[#DCDCDC] dark:border-gray-600 rounded-lg font-nunito dark:text-white",
                        errors.addresses?.[index]?.city && "border-[#EC5454]",
                      )}
                    />
                  )}
                />
                <FormErrorText
                  message={errors.addresses?.[index]?.city?.message}
                  className="mt-[7px] font-semibold font-nunito"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div>
                <RequiredLabel
                  htmlFor={`state-${field.id}`}
                  className="block text-[#75757D] dark:text-gray-400 text-sm font-nunito mb-2"
                  required
                >
                  {t("merchantRegister.step4.state")}
                </RequiredLabel>
                <Controller
                  name={`addresses.${index}.state`}
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id={`state-${fields[index].id}`}
                      placeholder=""
                      className={cn(
                        "h-12 bg-white dark:bg-gray-800 border border-[#DCDCDC] dark:border-gray-600 rounded-lg font-nunito dark:text-white",
                        errors.addresses?.[index]?.state && "border-[#EC5454]",
                      )}
                    />
                  )}
                />
                <FormErrorText
                  message={errors.addresses?.[index]?.state?.message}
                  className="mt-[7px] font-semibold font-nunito"
                />
              </div>

              <div>
                <RequiredLabel
                  htmlFor={`zipcode-${field.id}`}
                  className="block text-[#75757D] dark:text-gray-400 text-sm font-nunito mb-2"
                  required
                >
                  {t("merchantRegister.step4.zipcode")}
                </RequiredLabel>
                <Controller
                  name={`addresses.${index}.zipcode`}
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id={`zipcode-${fields[index].id}`}
                      placeholder=""
                      onBlur={() => {
                        field.onBlur();
                      }}
                      className={cn(
                        "h-12 bg-white dark:bg-gray-800 border border-[#DCDCDC] dark:border-gray-600 rounded-lg font-nunito dark:text-white",
                        errors.addresses?.[index]?.zipcode && "border-[#EC5454]",
                      )}
                    />
                  )}
                />
                <FormErrorText
                  message={errors.addresses?.[index]?.zipcode?.message}
                  className="mt-[7px] font-semibold font-nunito"
                />
              </div>
            </div>
          </div>
        ))}

        {/* Add Another Address Button */}
        <button
          type="button"
          onClick={handleAddAddress}
          className={cn(
            "w-full py-3 rounded-lg",
            "border-[1.4px] border-dashed border-[#004232] dark:border-[#00C49A]",
            "bg-[#D3F5EE] dark:bg-[#0d2818]",
            "text-[#004232] dark:text-[#00C49A] font-bold font-nunito",
            "flex items-center justify-center gap-2",
            "hover:bg-[#C0EDE4] dark:hover:bg-[#0a2015] transition-colors",
          )}
        >
          <Plus className="w-5 h-5" />
          {t("merchantRegister.step4.addAnotherAddress")}
        </button>
      </div>

      <div className="flex-1 flex items-end justify-end">
        <MerchantStepButtons
          previousBtn={{
            label: t("merchantRegister.buttons.back"),
            onClick: () => navigate("/merchant/register/verify-email"),
          }}
          nextBtn={{
            label: t("merchantRegister.buttons.next"),
            loading: completeOnboarding.isLoading,
            onClick: () => void handleSubmit(onSubmit)(),
          }}
        />
      </div>

      <LocationPicker
        isOpen={isOpen}
        onClose={() => setOpen(false)}
        modalAction={{
          label: t("merchantRegister.step4.done"),
          onClick: () => setOpen(false),
        }}
        value={location}
        enableSearch
        enableCurrentLocation
        enableMarker
        onChange={(nextLocation) => {
          setLocation(nextLocation);
          const setAddressField = (
            field: "address1" | "city" | "state" | "zipcode",
            value: string | undefined,
          ) => {
            if (!value) return;
            setValue(`addresses.${activeAddressIndex}.${field}`, value, {
              shouldValidate: true,
              shouldDirty: true,
            });
          };
          setAddressField("address1", nextLocation.address);
          setAddressField("city", nextLocation.city);
          setAddressField("state", nextLocation.state);
          setAddressField("zipcode", nextLocation.postalCode);
          setValue(`addresses.${activeAddressIndex}.latitude`, nextLocation.latitude, {
            shouldDirty: true,
          });
          setValue(`addresses.${activeAddressIndex}.longitude`, nextLocation.longitude, {
            shouldDirty: true,
          });
        }}
      />
    </div>
  );
};

export default Step4BusinessDetailsForm;
